/* Bambody v4 — mobile variant pickers as brand-styled dropdowns.
 *
 * WHY: on a phone the Style / Size / Pack pill grids run 3+ rows each, which
 * made the shop block and the PDP enormously tall. A dropdown collapses each
 * to one 48px control. A native <select> did that but rendered the OS list,
 * which looks nothing like the rest of the site — so this is a custom listbox
 * styled from the theme tokens.
 *
 * HOW: this does NOT touch bambody-v3-shop.js / bambody-v3-pdp.js — those are
 * shared with the LIVE v3 theme. It mirrors each pill row into a listbox and
 * forwards a selection to the corresponding button's own click handler, so all
 * selection/availability/pricing logic stays exactly where it was.
 *
 * The v3 renderers rebuild these rows with innerHTML on every selection, so a
 * MutationObserver re-syncs after each render. Desktop keeps the pills — CSS
 * decides which of the two is visible, so there is no breakpoint logic here
 * and nothing to re-wire on resize.
 *
 * Colour is deliberately left as swatches: it is already a single compact row,
 * and the swatch chips carry information a text dropdown would throw away.
 */
(function () {
  'use strict'

  // [row id, accessible label, selector for the option label inside a button]
  var GROUPS = [
    ['bam-v3-style-group', 'Style', null],
    ['bam-v3-size-row', 'Size', null],
    ['bam-v3-pack-row', 'Pack', '.pack-name'],
    ['bam-v3-pdp-size-row', 'Size', null],
    ['bam-v3-pdp-pack-row', 'Pack', '.pack-name'],
  ]

  var CHEVRON =
    '<svg class="bam-v4-sel__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="6 9 12 15 18 9"/></svg>'

  var CHECK =
    '<svg class="bam-v4-sel__tick" width="15" height="15" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="20 6 9 17 4 12"/></svg>'

  var openMenu = null

  function buttonsOf(row) {
    return Array.prototype.slice.call(row.querySelectorAll('button'))
  }

  function labelOf(btn, labelSel) {
    var node = labelSel ? btn.querySelector(labelSel) : null
    return ((node || btn).textContent || '').trim()
  }

  function close(sel) {
    if (!sel) return
    sel.classList.remove('is-open')
    sel.querySelector('.bam-v4-sel__btn').setAttribute('aria-expanded', 'false')
    sel.querySelector('.bam-v4-sel__list').hidden = true
    if (openMenu === sel) openMenu = null
  }

  function open(sel) {
    if (openMenu && openMenu !== sel) close(openMenu)
    sel.classList.add('is-open')
    sel.querySelector('.bam-v4-sel__btn').setAttribute('aria-expanded', 'true')
    var list = sel.querySelector('.bam-v4-sel__list')
    list.hidden = false
    openMenu = sel
    var active = list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]')
    if (active) active.focus()
  }

  function choose(sel, row, index) {
    var btn = buttonsOf(row)[index]
    close(sel)
    sel.querySelector('.bam-v4-sel__btn').focus()
    if (btn) btn.click() // hand off to the v3 handler — no logic duplicated here
  }

  function sync(sel, row, labelSel) {
    var btns = buttonsOf(row)
    if (!btns.length) {
      sel.hidden = true
      return
    }
    sel.hidden = false

    var list = sel.querySelector('.bam-v4-sel__list')
    // Rebuild only when the option set actually changed — otherwise every
    // re-render would blow away the list and steal focus mid-interaction.
    // data-hint (PDP sizes in stock only in another colour, e.g. "In Black")
    // shows where the tick would, so phones get the same cue as the dashed pill.
    var signature = btns
      .map(function (b) {
        return labelOf(b, labelSel) + '|' + (b.classList.contains('oos') || b.disabled ? '1' : '0') + '|' + (b.getAttribute('data-hint') || '')
      })
      .join('~')

    if (list.dataset.signature !== signature) {
      list.dataset.signature = signature
      list.innerHTML = btns
        .map(function (b, i) {
          var oos = b.classList.contains('oos') || b.disabled
          var hint = b.getAttribute('data-hint')
          return (
            '<li role="option" tabindex="-1" data-index="' + i + '"' +
            (oos ? ' aria-disabled="true"' : '') + ' class="bam-v4-sel__opt">' +
            '<span class="bam-v4-sel__opt-label">' + labelOf(b, labelSel) + '</span>' +
            (oos ? '<span class="bam-v4-sel__oos">Sold out</span>' : hint ? '<span class="bam-v4-sel__hint">' + hint + '</span>' : CHECK) +
            '</li>'
          )
        })
        .join('')
    }

    var activeIndex = btns.findIndex(function (b) {
      return b.classList.contains('active')
    })
    if (activeIndex < 0) activeIndex = 0
    Array.prototype.forEach.call(list.children, function (li, i) {
      li.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false')
    })
    sel.querySelector('.bam-v4-sel__val').textContent = labelOf(btns[activeIndex], labelSel)
  }

  function attach(id, label, labelSel) {
    var row = document.getElementById(id)
    if (!row || row.dataset.bamV4Select === '1') return
    row.dataset.bamV4Select = '1'

    var sel = document.createElement('div')
    sel.className = 'bam-v4-sel'
    sel.innerHTML =
      '<button type="button" class="bam-v4-sel__btn" aria-haspopup="listbox" aria-expanded="false" ' +
      'aria-label="' + label + '"><span class="bam-v4-sel__val"></span>' + CHEVRON + '</button>' +
      '<ul class="bam-v4-sel__list" role="listbox" aria-label="' + label + '" hidden></ul>'
    row.parentNode.insertBefore(sel, row)

    var trigger = sel.querySelector('.bam-v4-sel__btn')
    var list = sel.querySelector('.bam-v4-sel__list')

    trigger.addEventListener('click', function (e) {
      e.stopPropagation()
      sel.classList.contains('is-open') ? close(sel) : open(sel)
    })

    list.addEventListener('click', function (e) {
      var li = e.target.closest('[role="option"]')
      if (!li || li.getAttribute('aria-disabled') === 'true') return
      choose(sel, row, Number(li.dataset.index))
    })

    // Keyboard: the whole point of a custom listbox is that it must still
    // behave like the native control it replaced.
    sel.addEventListener('keydown', function (e) {
      var opts = Array.prototype.filter.call(list.children, function (li) {
        return li.getAttribute('aria-disabled') !== 'true'
      })
      var here = opts.indexOf(document.activeElement)
      if (e.key === 'Escape') {
        close(sel)
        trigger.focus()
      } else if (!sel.classList.contains('is-open')) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open(sel)
        }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        var next = here + (e.key === 'ArrowDown' ? 1 : -1)
        if (next < 0) next = opts.length - 1
        if (next >= opts.length) next = 0
        if (opts[next]) opts[next].focus()
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        var li = document.activeElement.closest('[role="option"]')
        if (li) choose(sel, row, Number(li.dataset.index))
      }
    })

    sync(sel, row, labelSel)
    new MutationObserver(function () {
      sync(sel, row, labelSel)
    }).observe(row, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] })
  }

  /* Rating: the shop block renders bare fallback stars with no score. Add the
   * grade beside them — but only while Judge.me has NOT hydrated its own badge
   * (which carries a real score and would then be the source of truth). */
  function rating() {
    var stars = document.getElementById('bam-v3-shop-stars-fallback')
    if (!stars) return
    var grade = document.getElementById('bam-v4-grade')
    if (!grade) {
      grade = document.createElement('span')
      grade.id = 'bam-v4-grade'
      grade.className = 'bam-v4-grade'
      grade.innerHTML =
        '<span class="bam-v4-grade__num">4.8</span>' +
        '<span class="bam-v4-grade__sep" aria-hidden="true">\u00b7</span>' +
        '<span class="bam-v4-grade__count">12,400+ reviews</span>'
      stars.parentNode.insertBefore(grade, stars.nextSibling)
    }
    grade.hidden = stars.offsetParent === null
  }

  function init() {
    GROUPS.forEach(function (g) {
      attach(g[0], g[1], g[2])
    })
    rating()
  }

  document.addEventListener('click', function () {
    close(openMenu)
  })

  // The row elements are static section markup, so they exist at DOMContentLoaded
  // even though the v3 scripts fill them with buttons later — the per-row
  // observer above covers that.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
  var note = document.querySelector('.shop-note')
  if (note) new MutationObserver(rating).observe(note, { childList: true, subtree: true })
})()
